<template>
    <BaseModal @close="closeModal" :visible="true" size="large" customClass="select__item__popup">
        <template #title>
            <div class="title__cover__box">
                <h4 class="title">Please Remove {{ group.totalCount - group.selectionCount }} Items
                    <span class="selecteditem">
                        Skipping Items ({{ selectedQuestionIds?.length }} of {{ group.totalCount }})
                    </span>
                </h4>
                <div class="random__btn">
                    <button type="button" class="pink__solid__btn" @click="randomizeSelection">Randomize</button>
                </div>
            </div>
        </template>
        <div class="divider__sepline"></div>
        <h6 class="title rlms-fs-xs rlms-text-center">
            Please Remove {{ group.totalCount - group.selectionCount }} items
            from
            these {{ group.totalCount }} items.</h6>

        <div class="select__item__box__wrapper">
            <div v-for="(question, index) in questions" :key="question.id" class="select__data__item"
                :class="{ 'remove__item': selectedQuestionIds.includes(question.id) }">
                <div class="check__btn">
                    <input type="checkbox" :id="`question_${question.id}`" :value="question.id"
                        :checked="selectedQuestionIds.includes(question.id)"
                        @change="(e) => handleCheckboxChange(e, question.id)" />
                </div>
                <label class="select__text__box" :for="`question_${question.id}`">
                    <span class="item__number">{{ index + 1 }}.</span>
                    <div class="text__wrapper">
                        <h6 class="label">{{ question.question }}</h6>
                        <p>{{ formatConsonants(question.consonant) }}</p>
                    </div>
                </label>

            </div>
        </div>

        <template #footer>
            <div class="popup__btn__box rlms-flex rlms-gap-30 rlms-justify-center">
                <div class="purple__trans__btn">
                    <a href="javascript:void(0)" @click="closeModal">Cancel</a>
                </div>
                <div class="purple__solid__btn">
                    <a href="javascript:void(0);" @click="confirm">Confirm</a>
                </div>
            </div>
        </template>
    </BaseModal>
</template>

<script setup>
import { ref } from 'vue';
import BaseModal from '../BaseModal.vue';
import api from '@/services/axios';
import confirmationIcon from '@/assets/images/confirmation.png';
import { showConfirmDialog, showToast } from '@/utils/swal';
import { formatConsonants } from '@/utils/format-helpers';
import { handleError } from '@/utils/handleError';

// eslint-disable-next-line
const emit = defineEmits(['close', 'saved']);

// eslint-disable-next-line
const props = defineProps({
    questions: {
        type: Array,
        required: true
    },
    group: {
        type: Object,
        required: true
    },
    organizationId: {
        type: Number,
        required: true,
    },
    assessmentId: {
        type: Number,
        required: true,
    },
});

// const title = `If this learner can accurately repeat 2-syllable phrases, click Yes, and 
//     the app will give this learner full credit for Groups 1 and 2, as per Dr. Esch, and take you to Group 3.`;

const showModal = ref(true);
const selectedQuestionIds = ref([]);

const closeModal = () => {
    showModal.value = false;
    emit('close');
};

// const handleModal = (action = 'close') => {
//     showModal.value = false;
//     emit(action);
// };

const handleCheckboxChange = (event, id) => {
    if (event.target.checked) {
        if (selectedQuestionIds.value.length >= props.group.totalCount - props.group.selectionCount) {
            event.preventDefault();
            event.target.checked = false;
            showToast({
                icon: 'warning',
                title: 'Warning!',
                text: `You can only remove ${props.group.totalCount - props.group.selectionCount} items.`
            });
            return;
        }
        selectedQuestionIds.value.push(id);
    } else {
        selectedQuestionIds.value = selectedQuestionIds.value.filter(qid => qid !== id);
    }
};

const randomizeSelection = async () => {
    const limit = props.group.totalCount - props.group.selectionCount;

    if (props.questions.length < limit) {
        showToast({
            icon: 'error',
            title: 'Error!',
            text: `Not enough questions to randomize.`
        });
        return;
    }

    const result = await showConfirmDialog({
        icon: 'warning',
        title: 'Are you sure?',
        html: `<div class="description__wrapper randomizedPopupDescritpion">You want to remove ${limit} items randomly from these ${props.group.totalCount} items.</div>`,
        // imageUrl: confirmationIcon,
        confirmButtonText: 'Remove Randomly',
    });

    if (result.isConfirmed) { 
        // Get shuffled question IDs
        const shuffled = [...props.questions].sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, limit).map(q => q.id);
    
        selectedQuestionIds.value = selected;
    
        showToast({
            icon: 'success',
            title: 'Success!',
            text: `Randomly removed selected ${limit} items.`
        });
    }

};

const confirm = async () => {
    const skipItems = (props.group.totalCount - props.group.selectionCount);
    if (selectedQuestionIds.value.length !== skipItems) {
        showToast({
            icon: 'warning',
            title: 'Warning!',
            text: `You can only remove ${skipItems} items.`
        });
        return;
    }

    const result = await showConfirmDialog({
        title: 'Are you sure?',
        html: '<p id="text-message">Change is not reversible and the ‘assessment’ will have to be deleted if you want to use different test items.</p>',
        confirmButtonText: 'Confirm',
        imageUrl: confirmationIcon
    });

    if (result.isConfirmed) {
        try {
            await api.put(`/organizations/${props.organizationId}/assessments/${props.assessmentId}`, {
                [`skip_group${props.group.id}_qid`]: selectedQuestionIds.value
            });

            showToast({
                icon: 'success',
                title: 'Success!',
                text: `The test items have been selected successfully. You may now begin the assessment.`
            });

            emit('saved');
        } catch (error) {
            handleError(error);
        }
    }
};
</script>
